/**
 * The four places money changes hands — the black market, the Gold shop, the
 * banker and the vendor's own rows.
 *
 * One slice of the base-view stylesheet. `styles/index.ts` concatenates
 * every slice into the single sheet `injectStyles` mounts — the split is
 * about OWNERSHIP, not about emitting more than one style element.
 *
 * Everything shared with non-commerce windows (the scrim, the frame, the close
 * cross, the title, the error line, the buttons) lives in `window.css.ts`.
 * What is here is only what these four do differently from a plain window, and
 * it is small on purpose: four shopfronts should read as four counters in one
 * settlement, not four different games.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below
 * is one template literal and a stray backtick terminates it. That has
 * broken the build twice, both times with esbuild pointing at a line ten
 * rows away from the cause. Write class names in plain text.
 */
export default `
/* ── Stock lists ──────────────────────────────────────────────────────────
   One row per thing for sale — name and price on the left, the button on the
   right, at a fixed rhythm so a long catalogue scans. Still used by the
   vendor stalls; the black market moved to the card grid below. */
.bv-shop-list { display: flex; flex-direction: column; gap: 4px; }
.bv-shop-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; padding: 7px 9px;
}
.bv-shop-label { color: var(--bone); font: 0.82rem var(--font-body); }

/* ── The stall's header: the trader, and the restock clock ────────────────
   Janeks: "in Trading UI, highlight the Trading timer. Put it above the
   trading slots, vertically the same place where trader description is and
   larger."

   A two-column row rather than a floated badge, so the plate stays level with
   the trader's own line at every width and falls UNDER it (rather than
   overlapping the name) on a narrow one. The value is the largest number in
   the panel, tabular so it does not jitter as it counts down, and the label
   above it says what the number is for -- a bare clock in a shop reads as an
   offer expiring, which is the opposite of what a restock is. */
.bv-vendor-head {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 14px; flex-wrap: wrap;
  /* Clears the window's close cross, which is absolute at top 6 / right 10
     INSIDE the frame's own 18px padding. Without this the plate and the x
     shared the same corner -- seen in a screenshot, not in the CSS. */
  padding-right: 24px;
}
.bv-vendor-head-text { min-width: 0; }
.bv-vendor-head-text .bv-win-sub { margin-bottom: 0; }
.bv-vendor-rot {
  display: flex; flex-direction: column; align-items: flex-end; gap: 1px;
  flex: none; padding: 5px 12px 6px;
  border: 1px solid var(--brass); border-radius: var(--radius-well);
  background: linear-gradient(168deg, rgba(122,90,48,0.34), rgba(26,18,10,0.6));
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.16), 0 3px 10px rgba(0,0,0,0.45);
}
.bv-vendor-rot-label {
  font: 700 0.6rem var(--font-display); letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--bone-dim);
}
.bv-vendor-rot-value {
  font: 700 1.35rem/1 var(--font-display); letter-spacing: 0.02em;
  color: var(--brass-hot); font-variant-numeric: tabular-nums;
  text-shadow: 0 0 12px rgba(255,189,92,0.35), 0 1px 2px rgba(0,0,0,0.6);
}

/* ── Black market: the card grid ──────────────────────────────────────────
   Janeks asked for cards under category tabs rather than the text list this
   panel shipped with. The two standing offers (the deed, hideout space) sit
   ABOVE the tabs, because neither is an item and neither belongs to a
   category — putting them behind a tab click would have hidden the biggest
   purchase in the game to gain nothing. */
.bv-shop-body { display: flex; flex-direction: column; gap: 10px; }
.bv-shop-wallet { display: flex; gap: 14px; align-items: baseline; margin: 0; }
/* 22px of headroom, because the offer art hangs ABOVE its card -- see
   .bv-shop-offer-art. Without it the bags and crates are sheared off by the
   window's own scroll box. */
.bv-shop-offers {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 8px; padding-top: 22px;
}
.bv-shop-offers .bv-shop-deed { margin-bottom: 0; }
/* The top-up card. Same frame as the deed and the hideout slot -- it is the
   third standing offer -- with a gold rim, because it is the one that leads
   to the till. */
.bv-shop-topup {
  display: flex; flex-direction: column;
  padding: 10px 11px; border-color: var(--brass-hot);
  box-shadow: 0 0 0 1px rgba(255,189,92,0.18), 0 0 16px rgba(220,176,106,0.12),
              inset 0 1px 0 rgba(255,255,255,0.05);
}
/* Pinned to the bottom of the card so the three offers' buttons line up even
   though their blurbs differ in length. */
.bv-shop-topup-btn { align-self: flex-start; margin-top: auto; }
.bv-shop-space { padding: 10px 11px; border-color: var(--brass); }

/* ── Depth ────────────────────────────────────────────────────────────────
   Janeks: "style the buttons, everything there is too flat. Work on the
   market design - less flat, more thematic, add depth. Item icons can be
   larger and overflowing and moving on card hover."

   Everything below is scoped to .bv-shop-*. The shared .panel / .well /
   .bv-act live in window.css.ts and app.css and are used by the vendor, the
   banker, the Gold shop and the world map as well -- and this file's own
   header argues that four shopfronts should read as four counters in one
   settlement. So the black market gets a heavier version of the kit, not a
   different kit.

   ── The art hangs over the card ─────────────────────────────────────────
   Absolute, anchored to the card's top-right, and pulled UP out of it. The
   card therefore needs no extra height and the text below it is undisturbed;
   what pays for the overhang is the padding-top on the strip and the grid
   above and below. A drop-shadow (the filter, not box-shadow) follows the
   art's own alpha, which is why these had to be cut out properly first. */
.bv-shop-offer { position: relative; }
.bv-shop-offer-art {
  position: absolute; top: -34px; right: -10px;
  width: 88px; height: 88px; object-fit: contain;
  pointer-events: none;
  filter: drop-shadow(0 6px 10px rgba(0,0,0,0.65)) drop-shadow(0 1px 2px rgba(0,0,0,0.5));
  transition: transform 0.18s ease;
}
/* Room for it. Measured off the shot, not guessed: at 88px hung 34px above
   the card, the art still reaches about 54px down the inside of it, which is
   the heading and the blurb's first two lines. The first pass gave the blurb
   30px and the sentence ran under the crate. */
.bv-shop-offer .bv-shop-deed-head { padding-right: 82px; }
.bv-shop-offer .bv-shop-deed-blurb { padding-right: 62px; }
.bv-shop-offer:hover .bv-shop-offer-art { transform: translateY(-3px) rotate(-2deg); }

.bv-shop-tabs {
  display: flex; flex-wrap: wrap; gap: 4px;
  border-bottom: 1px solid var(--line); padding-bottom: 6px;
}
.bv-shop-tab {
  padding: 4px 11px; cursor: pointer;
  background: transparent; border: 1px solid transparent;
  border-radius: var(--radius-tape);
  font: 0.78rem var(--font-display); letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--bone-dim);
}
.bv-shop-tab:hover { color: var(--bone); }
.bv-shop-tab-on {
  color: var(--brass-hot); border-color: var(--brass);
  background: rgba(210,164,86,0.08);
}

/* Wide enough for the art, the name and a two-line clock without reflowing
   when a countdown ticks from 1h 30m to 1h 29m.

   The padding is what the art's overhang lives in. This grid is a SCROLL BOX
   (overflow-y), so anything crossing its edge is clipped -- an icon lifted
   out of a card in the top row would be sheared off at exactly the moment a
   player hovered it. 18px of headroom is more than the lift, and the cards
   are pulled back up by the same amount so nothing moves on screen. */
.bv-shop-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(152px, 1fr));
  gap: 8px 8px; align-content: start; overflow-y: auto; max-height: 52vh;
  padding: 18px 2px 4px;
}
/* ── One card ─────────────────────────────────────────────────────────────
   A crate on a shelf, not a rectangle: a top-lit gradient, a hairline of
   light along the top edge, and a shadow underneath it that grows when the
   card is picked up. transform + box-shadow only on hover, both compositor
   properties, so a grid of thirty cards does not relayout on a mouse move. */
.bv-shop-card {
  display: flex; flex-direction: column; gap: 4px; padding: 9px 10px;
  position: relative;
  background: linear-gradient(168deg, rgba(58,44,28,0.55), rgba(20,14,9,0.75));
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.09), 0 3px 8px rgba(0,0,0,0.45);
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}
.bv-shop-card:hover {
  transform: translateY(-3px);
  border-color: var(--brass);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.16), 0 10px 20px rgba(0,0,0,0.55),
              0 0 0 1px rgba(210,164,86,0.22);
}
.bv-shop-card-top {
  position: relative; display: flex; align-items: flex-end; justify-content: center;
  height: 78px;
}
/* Bigger than the box that holds it, and lifted out of the card's top on
   hover. transform-origin is the BOTTOM so the art grows upward into the
   headroom the grid reserves rather than down through the name beneath it.
   The shadow is a filter, so it traces the item's own silhouette. */
.bv-shop-card-art {
  max-width: 112px; max-height: 96px; margin-top: -18px; object-fit: contain;
  transform-origin: 50% 100%;
  filter: drop-shadow(0 5px 7px rgba(0,0,0,0.55));
  transition: transform 0.16s ease, filter 0.16s ease;
}
.bv-shop-card:hover .bv-shop-card-art {
  transform: scale(1.12) translateY(-4px);
  filter: drop-shadow(0 10px 12px rgba(0,0,0,0.6));
}
/* An item whose icon is not on disk yet. A neutral mark, NOT the item's name:
   the name is already printed directly underneath, and the slot grid's
   name-in-the-box fallback exists because a slot has no label of its own. This
   card does. Marked aria-hidden so a screen reader hears the name once. */
.bv-shop-card-noart {
  color: var(--bone-faint); font: 1.4rem/1 var(--font-display); opacity: 0.45;
}
.bv-shop-card-qty {
  position: absolute; right: 0; bottom: 0;
  padding: 0 5px; border-radius: var(--radius-tape);
  background: rgba(10,7,4,0.85); border: 1px solid var(--line);
  font: 700 0.72rem var(--font-technical); color: var(--bone);
  font-variant-numeric: tabular-nums;
}
.bv-shop-card-name { color: var(--bone); font: 0.82rem/1.25 var(--font-body); }
.bv-shop-card-boost { color: var(--moss); font: 0.72rem/1.3 var(--font-technical); }
.bv-shop-card-win { color: var(--bone-dim); font: 0.7rem/1.3 var(--font-technical); }
/* Spent, not merely low: the row cannot be bought again until it resets, and
   that is a block, not a stat. */
.bv-shop-card-win-spent { color: var(--warn); }
.bv-shop-card-offer { color: var(--brass-bright); font: 0.7rem/1.3 var(--font-note); }
.bv-shop-card-foot {
  display: flex; align-items: center; justify-content: space-between;
  gap: 6px; margin-top: auto; padding-top: 4px;
}
.bv-shop-buy { padding: 3px 12px; font-size: 0.76rem; }

/* ── The market's buttons ─────────────────────────────────────────────────
   Scoped by the PANEL id, not by a new class: every button in here is
   already a .bv-act from window.css.ts, and the three offer cards build
   theirs through the shared actionButton() helper. Re-selecting them under
   #shop-ui adds depth to all of them at once without a single call site
   learning a new class name -- and without touching the vendor, the banker or
   the Gold shop, which use the same .bv-act and should keep reading as the
   quieter counters they are.

   A pressed button MOVES. It is the one piece of feedback a flat button never
   gives, and the reason the whole panel read as a web form. */
#shop-ui .bv-act {
  border-color: rgba(210,164,86,0.35);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.14), 0 2px 5px rgba(0,0,0,0.45);
  text-shadow: 0 1px 1px rgba(0,0,0,0.45);
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease,
              box-shadow 0.12s ease, transform 0.08s ease;
}
#shop-ui .bv-act-go {
  background: linear-gradient(180deg, #ffd08a 0%, var(--brass-hot) 42%, #9a6c2c 100%);
  box-shadow: inset 0 1px 0 rgba(255,246,222,0.55), inset 0 -2px 0 rgba(88,56,18,0.55),
              0 3px 7px rgba(0,0,0,0.5);
}
#shop-ui .bv-act:hover:not(:disabled) {
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.2), 0 5px 12px rgba(0,0,0,0.5);
}
#shop-ui .bv-act-go:hover:not(:disabled) {
  background: linear-gradient(180deg, #ffe0aa 0%, #ffc178 42%, #a9793a 100%);
}
/* Pressed: down 1px, and the lift under it goes with it. */
#shop-ui .bv-act:active:not(:disabled) {
  transform: translateY(1px);
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.5);
}
#shop-ui .bv-act:disabled { box-shadow: none; text-shadow: none; }

/* The tabs, as a row of shelf labels rather than plain text. The selected one
   sits FORWARD of the others -- lit from above, with the dividing line under
   it erased -- which is what a tab strip is supposed to say. */
.bv-shop-tabs { position: relative; }
.bv-shop-tab {
  position: relative;
  background: linear-gradient(180deg, rgba(42,31,20,0.5), rgba(16,11,7,0.5));
  border-color: rgba(210,164,86,0.14);
  transition: color 0.12s ease, background 0.12s ease, border-color 0.12s ease,
              box-shadow 0.12s ease, transform 0.12s ease;
}
.bv-shop-tab:hover {
  background: linear-gradient(180deg, rgba(62,46,28,0.6), rgba(22,15,9,0.6));
  border-color: rgba(210,164,86,0.4);
  transform: translateY(-1px);
}
.bv-shop-tab-on {
  background: linear-gradient(180deg, rgba(122,90,48,0.42), rgba(52,36,18,0.42));
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.18), 0 2px 6px rgba(0,0,0,0.4);
}
/* The indicator GROWS out of the middle on hover and spans the whole tab when
   selected, so pointing at a tab already shows what clicking it will do. It
   sits on the strip's own dividing line (6px of padding plus the 1px border),
   which is why the selected tab appears to break that line rather than sit
   above it. */
.bv-shop-tab::after {
  content: ''; position: absolute; left: 50%; right: 50%; bottom: -7px; height: 2px;
  background: linear-gradient(90deg, transparent, var(--brass-hot), transparent);
  opacity: 0; pointer-events: none;
  transition: left 0.16s ease, right 0.16s ease, opacity 0.16s ease;
}
.bv-shop-tab:hover::after { left: 14%; right: 14%; opacity: 0.65; }
.bv-shop-tab-on::after { left: 0; right: 0; opacity: 1; }

/* ── Every interactive thing answers the pointer ──────────────────────────
   Janeks: "add hover effects on all interactive elements in the shop (buttons
   are static) also can add border effects to blocks. Its a premium shop.
   Gameify, attract attention."

   Three rules held throughout, because a shop that moves everywhere is noise
   rather than polish:

    - Only compositor properties animate (transform, opacity, box-shadow,
      filter). A grid of thirty cards must not relayout on a mouse move.
    - A DISABLED control answers nothing. A button that lifts and glows and
      then refuses the click is worse than one that never moved -- and the Buy
      button is disabled constantly here (short on Gold, allowance spent, offer
      lapsed), so this is the common case, not the corner.
    - Everything below degrades to a plain static state under
      prefers-reduced-motion, at the foot of this file. */

/* The sheen. One pass of light across a button when the pointer arrives --
   the whole reason it needs overflow: hidden and a stacking context. Driven
   by an animation rather than a transition so it always travels in ONE
   direction; a transition would run backwards on mouse-out, which reads as a
   glitch rather than as a highlight. */
#shop-ui .bv-act { position: relative; overflow: hidden; }
#shop-ui .bv-act::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(105deg, transparent 38%, rgba(255,246,222,0.42) 50%, transparent 62%);
  transform: translateX(-140%);
}
#shop-ui .bv-act:hover:not(:disabled)::after { animation: bv-shop-sheen 0.55s ease-out; }
@keyframes bv-shop-sheen {
  from { transform: translateX(-140%); }
  to { transform: translateX(140%); }
}

/* Lift and glow. The gold ring is the shop's own signal for "this one leads
   somewhere"; the vendor and the banker keep the quieter .bv-act they share. */
#shop-ui .bv-act:hover:not(:disabled) {
  transform: translateY(-1px);
  border-color: var(--brass-hot);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.24), 0 6px 14px rgba(0,0,0,0.5),
              0 0 0 1px rgba(255,189,92,0.35), 0 0 16px rgba(255,189,92,0.3);
}
#shop-ui .bv-act-go:hover:not(:disabled) {
  box-shadow: inset 0 1px 0 rgba(255,250,235,0.7), inset 0 -2px 0 rgba(88,56,18,0.5),
              0 6px 16px rgba(0,0,0,0.5), 0 0 20px rgba(255,189,92,0.45);
}
/* The dead ones stay dead — no lift, no ring, no sheen. */
#shop-ui .bv-act:disabled { transform: none; }
#shop-ui .bv-act:disabled::after { display: none; }
/* Keyboard parity: a focus ring that says the same thing the hover does. */
#shop-ui .bv-act:focus-visible,
.bv-shop-tab:focus-visible {
  outline: 2px solid var(--brass-hot); outline-offset: 2px;
}

/* ── The blocks ───────────────────────────────────────────────────────────
   Corner brackets, which is the oldest trick in game UI for saying "this is a
   panel you deal with" without adding a single word. Drawn as two L-shaped
   pseudo-elements rather than a border image so they can GROW and light up on
   hover; pointer-events are off so they never eat the click or the tooltip
   the card carries. */
.bv-shop-card::before,
.bv-shop-card::after {
  content: ''; position: absolute; width: 9px; height: 9px; pointer-events: none;
  border: 1px solid rgba(210,164,86,0.22);
  transition: width 0.16s ease, height 0.16s ease, border-color 0.16s ease,
              box-shadow 0.16s ease;
}
.bv-shop-card::before { top: 4px; left: 4px; border-right: 0; border-bottom: 0; }
.bv-shop-card::after { bottom: 4px; right: 4px; border-left: 0; border-top: 0; }
.bv-shop-card:hover::before,
.bv-shop-card:hover::after {
  width: 15px; height: 15px;
  border-color: var(--brass-hot);
  box-shadow: 0 0 7px rgba(255,189,92,0.4);
}
/* The price and the bundle badge come up with the card, so the eye lands on
   what the card COSTS rather than on the frame that moved. */
.bv-shop-card:hover .bv-shop-card-qty { border-color: var(--brass); color: var(--brass-hot); }
.bv-shop-card:hover .bv-shop-card-price { filter: brightness(1.18); }

/* The three standing offers are blocks too, and the biggest purchases on the
   screen. They lift as one piece, art included — the art's own tilt is on top
   of this, which is what makes the crate look like it is being picked up
   rather than sliding. */
.bv-shop-offer,
.bv-shop-deed {
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}
.bv-shop-offer:hover,
.bv-shop-deed:hover {
  transform: translateY(-2px);
  border-color: var(--brass-hot);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.16), 0 12px 24px rgba(0,0,0,0.55),
              0 0 0 1px rgba(255,189,92,0.28), 0 0 22px rgba(255,189,92,0.18);
}

/* The till breathes. ONE animated element in the whole panel, on the one card
   that leads to a real-money purchase — any more and the eye has nowhere to
   rest. On a pseudo-element ring rather than the card's own box-shadow,
   because an infinite animation beats a hover rule and would otherwise swallow
   the lift above. */
.bv-shop-topup::after {
  content: ''; position: absolute; inset: -1px; border-radius: inherit;
  pointer-events: none;
  box-shadow: 0 0 16px rgba(255,189,92,0.55), inset 0 0 12px rgba(255,189,92,0.12);
  opacity: 0.3;
  animation: bv-shop-breathe 3.6s ease-in-out infinite;
}
@keyframes bv-shop-breathe {
  0%, 100% { opacity: 0.22; }
  50% { opacity: 0.75; }
}

/* The grid scrolls, so its bar is part of the shop. Brass, like everything
   else the player can push. */
.bv-shop-grid::-webkit-scrollbar { width: 9px; }
.bv-shop-grid::-webkit-scrollbar-track {
  background: rgba(8,5,3,0.5); border-radius: var(--radius-tape);
}
.bv-shop-grid::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, var(--brass), #6a4d24);
  border-radius: var(--radius-tape);
  border: 1px solid rgba(255,189,92,0.18);
}
.bv-shop-grid::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, var(--brass-hot), var(--brass));
}

@media (prefers-reduced-motion: reduce) {
  /* Static states survive; everything that MOVES stops. The hover still says
     "this is live" through colour and the ring — nothing is lost but the
     motion, which is the point of the preference. */
  #shop-ui .bv-act,
  .bv-shop-card,
  .bv-shop-card-art,
  .bv-shop-offer,
  .bv-shop-deed,
  .bv-shop-tab { transition: none; }
  #shop-ui .bv-act:hover:not(:disabled),
  .bv-shop-card:hover,
  .bv-shop-offer:hover,
  .bv-shop-deed:hover,
  .bv-shop-tab:hover { transform: none; }
  .bv-shop-card:hover .bv-shop-card-art { transform: none; }
  #shop-ui .bv-act:hover:not(:disabled)::after { animation: none; }
  .bv-shop-topup::after { animation: none; opacity: 0.45; }
}

/* ── The deed ─────────────────────────────────────────────────────────────
   The black market's one non-item purchase, and by a wide margin its most
   expensive. Given the brass border the catalogue rows do not have, because it
   is the only card on the screen that is not a crate of something. */
.bv-shop-deed { padding: 10px 11px; margin-bottom: 8px; border-color: var(--brass); }
.bv-shop-deed-head {
  font: 700 0.95rem var(--font-display); letter-spacing: 0.04em;
  text-transform: uppercase; color: var(--brass-hot);
}
.bv-shop-deed-blurb {
  margin: 3px 0 8px; color: var(--bone-dim); font: 0.78rem/1.45 var(--font-body);
}
.bv-shop-deed-ask {
  margin: 0 0 7px; color: var(--warn); font: 0.8rem var(--font-note);
}

/* ── Gold packs ───────────────────────────────────────────────────────────
   A grid rather than a list: the packs are a ladder the player compares
   across, and a column of rows makes the biggest one look like just another
   line item. */
.bv-cshop-grid {
  /* EXACTLY eight columns, never auto-fill. Janeks: "Make pack selection cards
     in 1 line, so that the whole screen fits without scrolling." auto-fill
     wraps to a second row as soon as the window is narrow, which is the thing
     being asked against; a fixed count makes the cards share whatever width
     there is instead. minmax(0, 1fr) rather than a min px width, because a
     minimum is exactly what would force the wrap back. */
  display: grid; grid-template-columns: repeat(8, minmax(0, 1fr));
  /* One row now, so there is no row gap left to set; the top padding is the
     overhanging art's room. */
  gap: 12px; padding-top: 42px;
}

/* ── A pack card ──────────────────────────────────────────────────────────
   Janeks: *"Make the PAck cards more sleek ... Add visaul depth to all gold
   shop elements, the item icons can be larger, can even go over their
   container cards."* and *"Buy Button can be ditched from packs. Select pack -
   it is highlighted."*

   A real <button>, so the whole card is the control and the keyboard gets it
   for free. NOT the shared .well treatment any more: sleeker means one hairline
   and a gradient rather than a recessed box, and the depth comes from the
   shadow plus the art breaking the top edge. */
.bv-cshop-card {
  touch-action: manipulation;
  position: relative;
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  /* Reserves the room the art occupies INSIDE the card. The art itself is
     absolutely positioned (see .bv-cshop-art) so it takes no flex space —
     which is what keeps it off the Gold figure. 68 = the art's 96px height
     less the 30px it hangs above the card, plus a couple of px of air. */
  padding: 56px 6px 11px; cursor: pointer; text-align: center;
  background: linear-gradient(168deg, #2e2418 0%, var(--panel-a) 52%, #140f0a 100%);
  border: 1px solid var(--line); border-radius: var(--radius-panel);
  box-shadow: 0 5px 14px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05);
  transition: transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
}
.bv-cshop-card:hover {
  transform: translateY(-3px);
  border-color: var(--brass);
  box-shadow: 0 14px 28px rgba(0,0,0,0.6), 0 0 16px rgba(220,176,106,0.13),
              inset 0 1px 0 rgba(255,255,255,0.07);
}
.bv-cshop-card:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 3px; }
/* Selected: Janeks' "it is highlighted". A lit border, a warmer ground and a
   gold rim-glow, so it reads as chosen rather than merely hovered. */
.bv-cshop-card-on {
  border-color: var(--brass-hot);
  background: linear-gradient(168deg, #44341d 0%, #2a2116 54%, #17110b 100%);
  box-shadow: 0 10px 24px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,189,92,0.4),
              0 0 22px rgba(255,189,92,0.2), inset 0 1px 0 rgba(255,255,255,0.09);
}

/* The art box is just the positioning anchor. It takes no space at all — the
   first attempt gave it height: 0 and let the image hang on a negative
   margin, which LOOKED right and put the bottom 30px of every picture on top
   of its own Gold figure: a zero-height box still starts the flex flow at the
   card's top padding, so the margin only moved the image, not the text after
   it. Out of flow entirely is the fix. */
.bv-cshop-art-box {
  display: block; height: 0; pointer-events: none;
}
.bv-cshop-art {
  position: absolute; top: -30px; left: 50%;
  /* Sized against the narrowest column the eight-across row produces, not
     against the window: at 1060px the columns are ~118px, so 84px of art
     clears its own card and still overhangs it. max-width keeps it inside a
     column on a screen narrow enough that the 94vw cap bites. */
  width: 84px; height: 84px; max-width: 100%; object-fit: contain;
  /* The depth. A drop-shadow FILTER and not a box-shadow: the art is a cut-out
     with alpha, so a box shadow would draw a rectangle behind a nugget. */
  filter: drop-shadow(0 7px 9px rgba(0,0,0,0.6));
  transition: transform 0.16s ease, filter 0.16s ease;
}
/* translateX(-50%) is carried by EVERY transform on this element, because it
   is what centres it — a hover rule that set only scale() would drop the
   centring and slide the art half its width to the right. */
.bv-cshop-art { transform: translateX(-50%); }
.bv-cshop-card:hover .bv-cshop-art {
  transform: translateX(-50%) scale(1.07) translateY(-3px);
  filter: drop-shadow(0 11px 13px rgba(0,0,0,0.65));
}
.bv-cshop-card-on .bv-cshop-art {
  filter: drop-shadow(0 8px 10px rgba(0,0,0,0.6)) drop-shadow(0 0 13px rgba(255,189,92,0.42));
}
.bv-cshop-amount {
  /* Down from 1.35rem: the widest label is 143400 behind a coin, and at eight
     across it has ~106px of card to sit in. */
  font: 700 1.05rem/1 var(--font-display); color: var(--brass-hot);
  font-variant-numeric: tabular-nums;
}
.bv-cshop-amount img { width: 0.95em; height: 0.95em; vertical-align: -0.12em; }
.bv-cshop-price {
  font: 0.82rem var(--font-technical); color: var(--bone-dim);
  font-variant-numeric: tabular-nums;
}
.bv-cshop-card-on .bv-cshop-price { color: var(--bone); }

/* ── The checkout ─────────────────────────────────────────────────────────
   Janeks: *"slider is like a checkout step, only always there"*, with *"a
   realistic Gold glow around it"* and the changing icon *"larger and flying
   over card there"*. */
.bv-cshop-checkout {
  position: relative; margin-top: 40px;
  display: flex; flex-direction: column; gap: 11px;
  padding: 14px 20px 16px;
  background: linear-gradient(168deg, #33281a 0%, var(--panel-a) 50%, #17110b 100%);
  border: 1px solid var(--brass); border-radius: var(--radius-panel);
  /* The gold glow. Two rings and a warm bloom rather than one big blur: a
     single soft shadow at this size reads as a smudge, while a tight rim plus
     a wide low-alpha bloom reads as light coming off metal. */
  box-shadow:
    0 0 0 1px rgba(255,189,92,0.22),
    0 0 24px rgba(220,176,106,0.16),
    0 16px 40px rgba(0,0,0,0.6),
    inset 0 1px 0 rgba(255,255,255,0.06);
}

/* Where the flying art lives. Height 0 and overflow visible, so the picture
   hangs above the checkout box the way the pack art hangs above its card. */
.bv-cshop-stage {
  /* A ROW, not a stack. Centred over the panel, the hero art was landing on
     top of a pack card and hiding its price — the row above is cards edge to
     edge, so there is no gap for it to hang in. Beside the Gold figure it
     occludes nothing, it uses the horizontal space Janeks asked for, and it
     costs far less height than clearing the cards vertically would (that
     needed ~85px more, which put the panel back into scrolling on a short
     screen).
     It still overhangs the top edge, by less — enough to read as sitting in
     front of the box rather than inside it. */
  position: relative;
  /* Left-aligned, not centred: the slider, the amount field and the note below
     all start at the same x, and a centred hero floating above them read as a
     separate object with empty margins either side. Starting it on the same
     line makes the panel one composition — hero top-left, Proceed bottom-right
     — and turns the space on the right into somewhere for the eye to travel
     rather than a gap. */
  display: flex; flex-direction: row; align-items: center;
  justify-content: flex-start; gap: 26px;
  padding-top: 10px;
}
.bv-cshop-float { position: relative; width: 176px; height: 140px; flex: none; pointer-events: none; }
.bv-cshop-float-art {
  /* Janeks: "I want the currently selected slider image slightly larger, so
     that it feels more heroic." 132 -> 176. It hangs 36px above its box, which
     is inside the 40px gap to the pack row — so it reads as standing in front
     of the panel without reaching the cards' price text. */
  position: absolute; top: -36px; left: 0;
  width: 176px; height: 176px; object-fit: contain;
  filter: drop-shadow(0 16px 22px rgba(0,0,0,0.62)) drop-shadow(0 0 28px rgba(255,189,92,0.34));
}
/* Going UP the ladder the new picture rises in from below; going down it
   settles in from above. The outgoing one is simply replaced -- patchCheckout
   swaps the node only when the rung changes, so there is no old node to
   animate out and nothing restarts mid-drag. */
/* The translateX(-50%) these used to carry is gone with the centring it
   existed for -- the art is flush left in its own box now, so a transform that
   does not mention X leaves it where it is. */
@keyframes bv-cshop-rise {
  from { opacity: 0; transform: translateY(30px) scale(0.86); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes bv-cshop-settle {
  from { opacity: 0; transform: translateY(-30px) scale(0.86); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.bv-cshop-float-in { animation: bv-cshop-rise 280ms ease-out both; }
.bv-cshop-float-in-down { animation: bv-cshop-settle 280ms ease-out both; }
@media (prefers-reduced-motion: reduce) {
  .bv-cshop-float-in, .bv-cshop-float-in-down { animation: none; }
}

/* Janeks: "The amount of gold is above the slider, next to the Gold icon."
   goldAmount() already puts the coin in front of the number, so this only has
   to make it the biggest thing in the section. */
.bv-cshop-total {
  margin: 0; text-align: left;
  font: 700 2.3rem/1.1 var(--font-display); color: var(--brass-hot);
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 18px rgba(255,189,92,0.3);
}
.bv-cshop-total img { width: 1.15em; height: 1.15em; vertical-align: -0.16em; }

/* ── The slider ───────────────────────────────────────────────────────────
   --cshop-thumb is the thumb's width, and it is a variable because the JS that
   positions the following label needs the same number: the label is offset by
   (0.5 - fraction) * thumb so it stays centred on the thumb at both ends of
   the track instead of overhanging them. */
.bv-cshop-slider-row {
  --cshop-thumb: 20px;
  display: flex; align-items: center; padding-top: 4px;
}
/* Room above for the bounds and below for the label that follows the thumb. */
.bv-cshop-track { position: relative; flex: 1; padding: 16px 0 24px; }
.bv-cshop-slider {
  appearance: none; width: 100%; height: 8px; margin: 0; cursor: pointer;
  background: linear-gradient(180deg, #120d08, #241b12);
  border: 1px solid var(--line); border-radius: 999px;
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.7);
}
.bv-cshop-slider::-webkit-slider-thumb {
  appearance: none; width: var(--cshop-thumb); height: var(--cshop-thumb);
  border-radius: 50%; cursor: grab;
  background: radial-gradient(circle at 36% 30%, #ffe0a8, var(--brass-hot) 42%, #8e6a2c);
  border: 1px solid #5c4230;
  box-shadow: 0 2px 6px rgba(0,0,0,0.65), 0 0 12px rgba(255,189,92,0.45),
              inset 0 1px 0 rgba(255,255,255,0.5);
}
.bv-cshop-slider::-webkit-slider-thumb:active { cursor: grabbing; }
.bv-cshop-slider::-moz-range-thumb {
  width: var(--cshop-thumb); height: var(--cshop-thumb);
  border-radius: 50%; cursor: grab;
  background: radial-gradient(circle at 36% 30%, #ffe0a8, var(--brass-hot) 42%, #8e6a2c);
  border: 1px solid #5c4230;
  box-shadow: 0 2px 6px rgba(0,0,0,0.65), 0 0 12px rgba(255,189,92,0.45);
}
.bv-cshop-slider:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 4px; }
/* Janeks: "The slider dot is followed by the EUR amount text." Positioned from
   JS — no CSS expression can read a range input's thumb offset. */
.bv-cshop-thumb-label {
  position: absolute; bottom: 0; transform: translateX(-50%);
  white-space: nowrap; pointer-events: none;
  padding: 1px 7px; border-radius: var(--radius-tape);
  background: rgba(12,8,5,0.8); border: 1px solid var(--line);
  color: var(--bone); font: 700 0.78rem var(--font-technical);
  font-variant-numeric: tabular-nums;
}
/* The amount's name (Gold Dust ... Shipment of Gold Bars), ahead of its price.
   Tablet and up only: on a phone the label has to stay a short price tag. */
.bv-cshop-tier-name { margin-right: 7px; color: var(--brass-hot); letter-spacing: 0.04em; }
@media (max-width: 767px) { .bv-cshop-tier-name { display: none; } }
@media (max-width: 720px) { .bv-cshop-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
.bv-cshop-bound {
  position: absolute; top: 0;
  color: var(--bone-faint); font: 0.7rem var(--font-technical);
  font-variant-numeric: tabular-nums;
}
.bv-cshop-bound-lo { left: 0; }
.bv-cshop-bound-hi { right: 0; }

/* ── The actions row ──────────────────────────────────────────────────────
   Janeks: "there is a lot of empty space ... Use thw whole horizontal sapce."
   Everything under the slider used to stack in the left half of a 1000px box.
   One row instead: the amount and the chips pack from the left, and Proceed is
   pushed to the right edge by an auto margin on the element before it, so the
   row spans the panel however wide it gets. align-items:end lines the
   controls up on their baselines rather than their boxes, which differ in
   height because only one of them has a label above it. */
.bv-cshop-actions {
  display: flex; align-items: end; flex-wrap: wrap; gap: 14px 26px;
}
.bv-cshop-commit { margin-left: auto; }
/* Wrapped (a narrow window), the auto margin has nothing to push against, so
   Proceed would sit where it fell. Full width on its own line instead. */
@media (max-width: 860px) {
  .bv-cshop-commit { margin-left: 0; width: 100%; }
  .bv-cshop-proceed { width: 100%; }
}

/* ── The typed amount ─────────────────────────────────────────────────────
   Janeks: "Have inputs styled properly for EUR value." The slider is coarse
   (100-cent steps); this is where an exact figure gets said. Styled as a
   recessed well with the currency mark set into it, so the symbol reads as
   part of the control rather than as the first character of the value. */
/* Stacked, so the label sits over the box and the row below can align the
   boxes rather than the labels. */
.bv-cshop-amount-field { display: flex; flex-direction: column; gap: 6px; }
.bv-cshop-amount-label {
  color: var(--bone-dim); font: 10px var(--font-technical);
  letter-spacing: 0.16em; text-transform: uppercase;
}
.bv-cshop-amount-box {
  display: inline-flex; align-items: stretch;
  background: var(--well); border: 1px solid var(--line);
  border-radius: var(--radius-well);
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.6);
  transition: border-color 0.14s ease, box-shadow 0.14s ease;
}
/* :focus-within and not :focus — the ring belongs to the whole control, and
   the thing that actually takes focus is the bare input inside it. */
.bv-cshop-amount-box:focus-within {
  border-color: var(--brass-hot);
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.6), 0 0 14px rgba(255,189,92,0.22);
}
.bv-cshop-amount-cur {
  display: flex; align-items: center; padding: 0 4px 0 15px;
  color: var(--brass-bright); font: 700 1.35rem var(--font-display);
}
.bv-cshop-amount-input {
  /* Janeks: "need to make the input larger. Max input is 1600.00 EUR Account
     for that."
     The widest value the field can hold is 1600.00 -- six digits and a
     separator. Sized in ch and then MEASURED in the browser, because ch is the
     advance width of a ZERO and says nothing about the decimal point: 8ch
     looked like seven characters plus slack and clipped the maximum value in
     practice (scrollWidth 81px against an 81px box). 11ch has room for the
     longest value with the caret beside it, checked at the real maximum
     rather than counted. */
  width: 11ch; padding: 11px 16px 11px 4px;
  background: none; border: 0; outline: none;
  color: var(--bone); font: 700 1.35rem var(--font-technical);
  font-variant-numeric: tabular-nums;
}
/* Chrome and Safari draw spinners on a numeric field the moment one is
   focused. This is type=text with inputmode=decimal precisely to avoid
   them — the rule is belt to that braces, and covers a future change of type. */
.bv-cshop-amount-input::-webkit-outer-spin-button,
.bv-cshop-amount-input::-webkit-inner-spin-button { appearance: none; margin: 0; }

/* ── Payment chips ────────────────────────────────────────────────────────
   Owned by ui/payment-chips.ts, which is written to lift into core/ — see its
   header for why it cannot live there from this repo. Keep this block with it
   if it moves. */
.bv-cshop-pay { display: flex; }
.bv-chips { display: flex; flex-wrap: wrap; gap: 10px; }
.bv-chip {
  display: inline-flex; align-items: center; gap: 8px;
  /* Roomier than it was, because the name is now the whole chip -- the logo
     that used to sit beside it was carrying half the width. */
  padding: 10px 20px; cursor: pointer;
  background: linear-gradient(170deg, #241b12, #15100a);
  border: 1px solid var(--line); border-radius: var(--radius-well);
  color: var(--bone-dim); font: 0.82rem var(--font-technical); letter-spacing: 0.04em;
  box-shadow: 0 3px 8px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04);
  transition: color 0.14s ease, border-color 0.14s ease, box-shadow 0.14s ease,
              transform 0.14s ease, background 0.14s ease;
}
.bv-chip:hover:not(:disabled) { color: var(--bone); border-color: var(--brass); transform: translateY(-1px); }
.bv-chip:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 2px; }
/* Janeks: "Selected is Highlighted + glow." */
.bv-chip.bv-chip-on {
  color: #1a1208; border-color: var(--brass-hot);
  background: linear-gradient(170deg, var(--brass-hot), var(--brass));
  box-shadow: 0 0 0 1px rgba(255,189,92,0.45), 0 0 18px rgba(255,189,92,0.32),
              0 4px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.4);
}
.bv-chip-label { line-height: 1; }
/* Shown, greyed, unselectable — the two unbuilt methods stay visible so the
   player can see they are coming, rather than being hidden. */
.bv-chip-off { color: var(--bone-faint); cursor: default; opacity: 0.6; }
.bv-chip-soon {
  padding: 1px 5px; border-radius: var(--radius-tape);
  border: 1px solid var(--line); color: var(--bone-faint);
  font-size: 0.62rem; letter-spacing: 0.12em; text-transform: uppercase;
}

/* ── Proceed ──────────────────────────────────────────────────────────────
   Its own button rather than the shared .bv-act-go, because it is the only
   thing on this panel that spends money and should not be one of a row of
   equals. */
.bv-cshop-proceed {
  touch-action: manipulation;
  align-self: flex-start; cursor: pointer;
  padding: 12px 26px;
  background: linear-gradient(168deg, #ffd08a, var(--brass-hot) 46%, #a97c2f);
  border: 1px solid var(--brass-bright); border-radius: var(--radius-well);
  color: #1a1208;
  font: 700 0.95rem var(--font-display); letter-spacing: 0.1em; text-transform: uppercase;
  box-shadow: 0 0 20px rgba(255,189,92,0.28), 0 6px 16px rgba(0,0,0,0.55),
              inset 0 1px 0 rgba(255,255,255,0.45);
  transition: filter 0.14s ease, transform 0.14s ease, box-shadow 0.14s ease;
}
.bv-cshop-proceed:hover:not(:disabled) {
  filter: brightness(1.08); transform: translateY(-1px);
  box-shadow: 0 0 28px rgba(255,189,92,0.4), 0 9px 20px rgba(0,0,0,0.6),
              inset 0 1px 0 rgba(255,255,255,0.5);
}
.bv-cshop-proceed:focus-visible { outline: 2px solid var(--bone); outline-offset: 3px; }
.bv-cshop-proceed:disabled {
  filter: none; transform: none; cursor: default;
  background: linear-gradient(168deg, #3a2f20, #241b12);
  border-color: var(--line); color: var(--bone-faint);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
}

/* ── The journal ──────────────────────────────────────────────────────────
   A column of sections with room between them. Here rather than in a slice of
   its own because it is three declarations; it moves the day the journal grows
   a layout worth naming. */

/* ── The banker ───────────────────────────────────────────────────────────
   One exchange, one direction (Gold to Cash, never back).

   Three bands, the same three the Gold shop checkout uses: the DEAL (what you
   give, what you get), the TRACK (the slider that sets it), and the ACTIONS
   (type it exactly, jump to a fraction, commit). Two money windows that do the
   same thing should look like they do the same thing. The class names are this
   panel's own rather than shared with bv-cshop-*, because the Gold shop's
   block is tuned to its own art and its own copy and generalising it would put
   a working panel at risk for no visible gain. */

/* ── The header ─────────────────────────────────────────────────────────── */
/* Title on the left, wallet on the right. The wallet used to be a bare line
   under the subtitle, which put the number the whole decision is bounded by in
   the least-looked-at place on the panel. */
.bv-banker-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 18px; flex-wrap: wrap;
}
.bv-banker-wallet {
  display: flex; flex-direction: column; align-items: flex-end; gap: 3px;
  padding: 7px 12px; flex: none;
  background: var(--well); border: 1px solid var(--line);
  border-radius: var(--radius-well);
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.55);
}
.bv-banker-wallet-label {
  color: var(--bone-faint); font: 10px var(--font-technical);
  letter-spacing: 0.16em; text-transform: uppercase;
}
.bv-banker-wallet-figs { font: 0.95rem var(--font-technical); white-space: nowrap; }
.bv-banker-dot { color: var(--bone-faint); padding: 0 8px; }

/* The rate is the banker's whole product, so it gets a strip of its own rather
   than a clause in the subtitle. */
.bv-banker-rate {
  display: flex; align-items: baseline; gap: 10px;
  margin: 12px 0 0; padding: 8px 12px;
  background: linear-gradient(170deg, rgba(171,138,83,0.14), rgba(0,0,0,0.18));
  border: 1px solid var(--line); border-left: 2px solid var(--brass);
  border-radius: var(--radius-well);
}
.bv-banker-rate-label {
  color: var(--bone-faint); font: 10px var(--font-technical);
  letter-spacing: 0.16em; text-transform: uppercase;
}
.bv-banker-rate-eq {
  color: var(--brass-bright); font: 700 1.05rem var(--font-display);
  font-variant-numeric: tabular-nums;
}
/* Pushed to the far end of the strip, which was otherwise half empty. It also
   moves the one-way warning off the subtitle, where it was a second sentence
   nobody reads, and onto the line that states the trade. */
.bv-banker-oneway {
  margin-left: auto; padding: 2px 8px;
  border: 1px solid var(--line); border-radius: var(--radius-tape);
  color: var(--bone-dim); font: 10px var(--font-technical);
  letter-spacing: 0.16em; text-transform: uppercase;
}

/* ── The body ───────────────────────────────────────────────────────────── */
.bv-banker-body { display: flex; flex-direction: column; gap: 14px; }

/* ── The deal ─────────────────────────────────────────────────────────────
   The two figures the decision is made on, side by side with the direction
   drawn between them. One-way is the rule this panel most needs a player to
   understand before they commit, and an arrow says it without a sentence. */
.bv-banker-deal {
  display: flex; align-items: center; gap: 18px; flex-wrap: wrap;
  margin-top: 4px; padding: 16px 20px;
  background: linear-gradient(168deg, #33281a 0%, var(--panel-a) 52%, #17110b 100%);
  border: 1px solid var(--brass); border-radius: var(--radius-panel);
  /* Two rings and a warm bloom rather than one big blur: a single soft shadow
     at this size reads as a smudge, a tight rim plus a wide low-alpha bloom
     reads as light coming off metal. */
  box-shadow:
    0 0 0 1px rgba(255,189,92,0.20),
    0 0 22px rgba(220,176,106,0.14),
    0 14px 34px rgba(0,0,0,0.55),
    inset 0 1px 0 rgba(255,255,255,0.05);
}
.bv-banker-side { display: flex; flex-direction: column; gap: 4px; min-width: 0; flex: 1 1 0; }
.bv-banker-side-get { align-items: flex-end; text-align: right; }
.bv-banker-side-label {
  color: var(--bone-faint); font: 10px var(--font-technical);
  letter-spacing: 0.16em; text-transform: uppercase;
}
.bv-banker-give, .bv-banker-get {
  font: 700 2rem/1.1 var(--font-display);
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}
.bv-banker-give img { width: 1.05em; height: 1.05em; vertical-align: -0.14em; }
/* The unit beside the give figure, set smaller and dimmer so it labels the
   number rather than competing with it -- the receive side gets its unit from
   cashAmount at full size because Cash has no icon to carry it. */
.bv-banker-unit {
  margin-left: 6px; color: var(--bone-dim);
  font: 0.9rem var(--font-technical); letter-spacing: 0.06em;
}
.bv-banker-give .bv-win-gold { text-shadow: 0 0 18px rgba(255,189,92,0.3); }
.bv-banker-arrow { color: var(--brass); font: 1.6rem var(--font-display); flex: none; }

/* ── The slider ───────────────────────────────────────────────────────────
   --bank-thumb is the thumb width, and it is a variable because the JS that
   positions the label under it needs the same number: the label is offset by
   (0.5 - fraction) * thumb so it stays centred on the thumb instead of
   overhanging the ends of the track. */
.bv-banker-slider-row {
  --bank-thumb: 20px;
  display: flex; align-items: center;
}
/* Room above for the bounds, below for the label that follows the thumb. */
.bv-banker-track { position: relative; flex: 1; padding: 16px 0 24px; }
.bv-banker-slider {
  appearance: none; width: 100%; height: 8px; margin: 0; cursor: pointer;
  background: linear-gradient(180deg, #120d08, #241b12);
  border: 1px solid var(--line); border-radius: 999px;
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.7);
}
.bv-banker-slider::-webkit-slider-thumb {
  appearance: none; width: var(--bank-thumb); height: var(--bank-thumb);
  border-radius: 50%; cursor: grab;
  background: radial-gradient(circle at 36% 30%, #ffe0a8, var(--brass-hot) 42%, #8e6a2c);
  border: 1px solid #5c4230;
  box-shadow: 0 2px 6px rgba(0,0,0,0.65), 0 0 12px rgba(255,189,92,0.45),
              inset 0 1px 0 rgba(255,255,255,0.5);
}
.bv-banker-slider::-webkit-slider-thumb:active { cursor: grabbing; }
.bv-banker-slider::-moz-range-thumb {
  width: var(--bank-thumb); height: var(--bank-thumb);
  border-radius: 50%; cursor: grab;
  background: radial-gradient(circle at 36% 30%, #ffe0a8, var(--brass-hot) 42%, #8e6a2c);
  border: 1px solid #5c4230;
  box-shadow: 0 2px 6px rgba(0,0,0,0.65), 0 0 12px rgba(255,189,92,0.45);
}
.bv-banker-slider:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 4px; }
.bv-banker-slider:disabled { opacity: 0.45; cursor: default; }
.bv-banker-thumb-label {
  position: absolute; bottom: 0; transform: translateX(-50%);
  white-space: nowrap; pointer-events: none;
  padding: 1px 7px; border-radius: var(--radius-tape);
  background: rgba(12,8,5,0.8); border: 1px solid var(--line);
  color: var(--bone); font: 700 0.78rem var(--font-technical);
  font-variant-numeric: tabular-nums;
}
/* The bounds sit above the ENDS of the track. Beside it they would collide
   with the thumb label, which reaches the same x at either extreme. */
.bv-banker-bound {
  position: absolute; top: 0;
  color: var(--bone-faint); font: 0.7rem var(--font-technical);
  font-variant-numeric: tabular-nums;
}
.bv-banker-bound-lo { left: 0; }
.bv-banker-bound-hi { right: 0; }

/* ── The actions row ──────────────────────────────────────────────────────
   The typed field and the chips pack from the left; Exchange is pushed to the
   right edge by an auto margin, so the row spans the panel however wide it
   gets. align-items:end lines the controls up on their bottoms rather than
   their boxes, which differ in height because only one has a label above it. */
.bv-banker-actions {
  display: flex; align-items: end; flex-wrap: wrap; gap: 12px 20px;
}
.bv-banker-field { display: flex; flex-direction: column; gap: 6px; }
.bv-banker-label {
  color: var(--bone-dim); font: 10px var(--font-technical);
  letter-spacing: 0.16em; text-transform: uppercase;
}
/* A recessed well rather than a bare input, matching the Gold shop's amount
   box — the border ring belongs to the whole control, so it is :focus-within
   and not :focus. */
.bv-banker-box {
  display: inline-flex; align-items: stretch;
  background: var(--well); border: 1px solid var(--line);
  border-radius: var(--radius-well);
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.6);
  transition: border-color 0.14s ease, box-shadow 0.14s ease;
}
.bv-banker-box:focus-within {
  border-color: var(--brass-hot);
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.6), 0 0 14px rgba(255,189,92,0.22);
}
.bv-banker-input {
  width: 110px; padding: 9px 12px;
  background: none; border: none; outline: none;
  color: var(--bone); font: 700 1.2rem var(--font-display);
  font-variant-numeric: tabular-nums;
}
.bv-banker-input:disabled { color: var(--bone-faint); }

/* The fractions, MAX last. They are the reason the slider does not have to be
   dragged to a precise pixel for the two amounts anyone really picks -- half,
   and all of it. */
.bv-banker-quick { display: flex; gap: 6px; flex-wrap: wrap; }
.bv-banker-chip {
  touch-action: manipulation;
  padding: 8px 12px; cursor: pointer;
  background: rgba(60,48,31,0.35); color: var(--bone-dim);
  border: 1px solid var(--line); border-radius: var(--radius-tape);
  font: 700 0.72rem var(--font-technical); letter-spacing: 0.08em;
  transition: color 0.14s ease, border-color 0.14s ease, background 0.14s ease;
}
.bv-banker-chip:hover:not(:disabled) { color: var(--bone); border-color: var(--brass); }
.bv-banker-chip:disabled { opacity: 0.45; cursor: default; }
.bv-banker-chip-on {
  color: #1b1409; border-color: var(--brass-hot);
  background: linear-gradient(160deg, var(--brass-hot), var(--brass));
}

.bv-banker-commit { margin-left: auto; }
.bv-banker-go { padding: 11px 26px; font-size: 0.95rem; }
/* Wrapped (a narrow window) the auto margin has nothing to push against, so
   Exchange would sit wherever it fell. Full width on its own line instead. */
@media (max-width: 720px) {
  .bv-banker-commit { margin-left: 0; width: 100%; }
  .bv-banker-go { width: 100%; }
}

/* ── The confirm ──────────────────────────────────────────────────────────
   Sits ABOVE the banker window (1001) at 1002, alongside the player frame — an
   irreversible exchange must not be reachable behind anything, and every
   action button under it is disabled while it is up. */
.bv-banker-confirm { z-index: 1002; background: rgba(6,4,2,0.6); }
/* The same give -> receive line as the panel, so the thing being confirmed is
   recognisably the thing that was set. */
.bv-banker-confirm-line {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  margin: 14px 0 10px; padding: 12px 10px;
  background: var(--well); border: 1px solid var(--line);
  border-radius: var(--radius-well);
  font: 700 1.15rem var(--font-display); font-variant-numeric: tabular-nums;
}
.bv-banker-confirm-line img { width: 1.05em; height: 1.05em; vertical-align: -0.14em; }
/* nowrap on each side, not on the line: the line may wrap between the two
   figures on a very narrow window, but a figure must never be split from the
   unit that says what it is. */
.bv-banker-confirm-give, .bv-banker-confirm-get { white-space: nowrap; }
.bv-banker-confirm-acts { justify-content: flex-end; margin-top: 14px; }

/* ── Black market: wear and clutter ───────────────────────────────────────
   The house rule (CLAUDE.md, Wear and clutter): worn plate face, grime and
   scratches under the content, a sticker hanging off a corner, all
   pointer-events: none. The Atelier face keeps its own texture, so everything
   on the plate is scoped away from .bv-shop-atelier. */

/* The title sits OUTSIDE the plate, over the scrim, so the stage is a column
   of [title, plate]. The stage also carries the soft shadow behind the plate
   (::before) and the sticker (::after). */
.bv-shop-stage {
  position: relative; display: flex; flex-direction: column; align-items: center;
  max-width: 100%; max-height: 100%;
}
.bv-shop-stage::before {
  content: ''; position: absolute; z-index: 0; pointer-events: none;
  inset: 24px -56px -56px;
  background: radial-gradient(closest-side, rgba(0,0,0,0.62), rgba(0,0,0,0.3) 55%, transparent 100%);
  filter: blur(6px);
}
.bv-shop-stage::after {
  content: ''; position: absolute; z-index: 3; pointer-events: none;
  bottom: -16px; right: -22px; width: 76px; height: 76px;
  background: url('/clutter/clutter-b.webp') center / contain no-repeat;
  transform: rotate(9deg); filter: drop-shadow(0 3px 4px rgba(0,0,0,0.6));
}
.bv-shop-stage:has(.bv-shop-atelier)::after { display: none; }
.bv-shop-title {
  position: relative; z-index: 2; margin: 0 0 10px; padding: 0 8px; text-align: center;
  font: 800 clamp(1.3rem, 5vw, 1.9rem)/1.1 var(--font-display); letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--brass-hot);
  text-shadow: 0 2px 0 rgba(0,0,0,0.7), 0 0 18px rgba(255,189,92,0.35), 0 4px 14px rgba(0,0,0,0.8);
}
.bv-shop-title[hidden] { display: none; }
.bv-shop-stage > .bv-win { position: relative; z-index: 1; }
#shop-ui .bv-shop-plate:not(.bv-shop-atelier) {
  display: flex; flex-direction: column; padding: 12px 14px;
  max-height: calc(86vh - 3.4rem); max-height: calc(86dvh - 3.4rem);
  background:
    linear-gradient(rgba(14,9,5,0.74), rgba(14,9,5,0.86)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 20% 30% / 420px 420px,
    url('/clutter/plate.webp') 0 0 / 420px 420px, #17110a;
  border-color: var(--brass);
  box-shadow: 0 0 0 1px #120d08, 0 14px 34px rgba(0,0,0,0.6), inset 0 0 26px rgba(0,0,0,0.55);
}

/* ── The offers row: Deed | Buy Gold | Slots, one row ───────────────────
   The three existing containers share the shop card plate. They wrap and
   stack at narrow widths (one column at 320px). */
.bv-shop-offers {
  grid-template-columns: repeat(3, minmax(0, 1fr)); align-items: stretch;
}
.bv-shop-offers > .well {
  isolation: isolate; border: 1px solid var(--brass);
  background:
    linear-gradient(168deg, rgba(40,30,19,0.8), rgba(14,10,6,0.9)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 20% / 300px 300px,
    url('/clutter/plate.webp') 0 0 / 300px 300px, #17110a;
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.09), inset 0 0 14px rgba(0,0,0,0.5), 0 3px 8px rgba(0,0,0,0.45);
}
.bv-shop-offers .bv-act { min-height: 44px; min-width: 44px; touch-action: manipulation; }
.bv-shop-offers .bv-shop-deed-blurb { margin-bottom: 6px; }
/* The Buy Gold card: gold-ish, glowing, gently pulsing. */
#shop-ui .bv-shop-offers > .bv-shop-topup {
  border-color: #ffe39a;
  background:
    linear-gradient(168deg, rgba(120,84,12,0.62), rgba(40,26,4,0.86)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 20% / 300px 300px,
    url('/clutter/plate.webp') 0 0 / 300px 300px, #2a1c06;
  animation: bv-shop-buygold-glow 2.6s ease-in-out infinite;
}
#shop-ui .bv-shop-topup .bv-shop-deed-head { color: #ffe39a; }
#shop-ui .bv-shop-topup .bv-shop-deed-blurb { color: var(--bone); }
@keyframes bv-shop-buygold-glow {
  0%, 100% { box-shadow: inset 0 0 14px rgba(0,0,0,0.5), 0 0 8px rgba(255,205,70,0.3), 0 3px 8px rgba(0,0,0,0.45); }
  50% { box-shadow: inset 0 0 14px rgba(0,0,0,0.5), 0 0 22px rgba(255,214,90,0.7), 0 3px 8px rgba(0,0,0,0.45); }
}
@media (max-width: 640px) {
  .bv-shop-offers { grid-template-columns: 1fr; }
}

/* ── The default shop cards: worn plate, grime, scratches ────────────────
   Text stays on a 0.8+ dark tint, so bone on this face is well above 4.5:1. */
.bv-shop-card {
  isolation: isolate; overflow: visible;
  border: 1px solid var(--brass);
  background:
    linear-gradient(168deg, rgba(40,30,19,0.8), rgba(14,10,6,0.9)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 20% / 300px 300px,
    url('/clutter/plate.webp') 0 0 / 300px 300px, #17110a;
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.09), inset 0 0 14px rgba(0,0,0,0.5), 0 3px 8px rgba(0,0,0,0.45);
}
.bv-shop-card-name { color: var(--bone); }
.bv-shop-card-win { color: var(--bone-dim); }
.bv-shop-buy { min-height: 44px; min-width: 44px; padding: 6px 14px; touch-action: manipulation; }
.bv-shop-tab { min-height: 44px; touch-action: manipulation; }

@media (max-width: 480px) {
  .bv-shop-stage::before { inset: 20px -18px -22px; }
  .bv-shop-stage::after { width: 44px; height: 44px; right: -6px; top: 6px; }
  .bv-shop-grid { grid-template-columns: repeat(auto-fill, minmax(132px, 1fr)); }
}
@media (prefers-reduced-motion: reduce) {
  #shop-ui .bv-shop-offers > .bv-shop-topup { animation: none; box-shadow: inset 0 0 14px rgba(0,0,0,0.5), 0 0 14px rgba(255,205,70,0.4); }
}

/* ── Compact top, scrolling grid ──────────────────────────────────────────
   The plate is a flex column: header, offers and tabs keep their height, the
   grid takes what is left and scrolls on its own (dvh, so a short landscape
   phone gets the remainder). The plate keeps overflow-y as a last resort. */
#shop-ui .bv-shop-plate:not(.bv-shop-atelier) #shop-header { flex: none; }
#shop-ui .bv-shop-plate:not(.bv-shop-atelier) .bv-win-body { flex: 1 1 auto; min-height: 0; margin-top: 6px; }
#shop-ui .bv-shop-plate:not(.bv-shop-atelier) .bv-shop-body { gap: 6px; }
#shop-ui .bv-shop-plate .bv-shop-fronts { margin: 0 50px 4px 0; gap: 6px; }
#shop-ui .bv-shop-plate .bv-shop-wallet { gap: 12px; }
.bv-shop-body .bv-shop-grid { flex: 1 1 auto; min-height: 150px; max-height: none; padding-top: 14px; }
.bv-shop-body > .bv-shop-tabs, .bv-shop-body > .bv-shop-offers { flex: none; }
.bv-shop-offers { padding-top: 12px; gap: 6px; }
.bv-shop-offers > .well { padding: 6px 9px; }
.bv-shop-offers .bv-shop-deed-head { font-size: 0.82rem; }
.bv-shop-offers .bv-shop-deed-blurb { margin: 1px 0 5px; font-size: 0.72rem; line-height: 1.3; }
.bv-shop-offers .bv-shop-offer-art { width: 54px; height: 54px; top: -20px; right: -6px; }
.bv-shop-offers .bv-shop-offer .bv-shop-deed-head { padding-right: 46px; }
.bv-shop-offers .bv-shop-offer .bv-shop-deed-blurb { padding-right: 30px; }
.bv-shop-tabs { padding-bottom: 4px; }
@media (max-height: 560px) {
  .bv-shop-offers .bv-shop-offer-art { display: none; }
  .bv-shop-offers .bv-shop-offer .bv-shop-deed-head,
  .bv-shop-offers .bv-shop-offer .bv-shop-deed-blurb { padding-right: 0; }
  .bv-shop-offers { padding-top: 4px; }
}

/* The disabled Black Market tab. */
#shop-ui .bv-shop-front.bv-shop-front-soon {
  opacity: 0.45; transform: none; cursor: not-allowed; filter: grayscale(0.6);
  display: inline-flex; flex-direction: column; align-items: center; justify-content: center;
  line-height: 1.05;
}
.bv-shop-soon-tag {
  font: 700 0.58rem var(--font-technical); letter-spacing: 0.12em; text-transform: uppercase;
  color: #2a1d10; background: rgba(255,240,200,0.55); border-radius: 3px; padding: 1px 5px; margin-top: 2px;
}

/* ── Phones: three tabs in one row, offers as compact rows ─────────────
   Stacked, the three offer cards ate the whole plate and pushed the card grid
   off screen at 375px. Now compact rows with the button beside the text, and the three
   storefront tabs share a single row beside the close cross. */
@media (max-width: 640px) {
  #shop-ui .bv-shop-plate .bv-shop-fronts { flex-wrap: nowrap; gap: 4px; margin-right: 46px; }
  #shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front {
    flex: 1 1 0; min-width: 0; min-height: 44px; padding: 4px 3px; font-size: 0.64rem;
    letter-spacing: 0.02em; line-height: 1.1; overflow-wrap: anywhere;
  }
  .bv-shop-soon-tag { font-size: 0.5rem; padding: 1px 3px; }
  /* One column of compact rows: text on the left, the button on the right. The
     old sideways-scrolling strip put the first card's left border under the
     scrollport edge and made the whole shop feel like it slid around. */
  .bv-shop-offers { grid-template-columns: 1fr; padding-right: 0; }
  .bv-shop-offers > .well {
    display: grid; grid-template-columns: minmax(0, 1fr) auto; column-gap: 8px; align-items: center;
  }
  .bv-shop-offers > .well .bv-shop-deed-head { grid-column: 1; margin: 0; }
  .bv-shop-offers > .well .bv-shop-deed-blurb { grid-column: 1; margin: 1px 0 0; }
  .bv-shop-offers > .well .bv-act { grid-column: 2; grid-row: 1 / span 2; align-self: center; }
  .bv-shop-offers .bv-shop-offer-art { display: none; }
}

/* Short landscape phones: spend the height on the card grid, not the offers. */
@media (max-height: 480px) {
  .bv-shop-offers .bv-shop-deed-blurb { display: none; }
  .bv-shop-offers .bv-shop-deed-head { margin-bottom: 4px; }
  .bv-shop-title { display: none; }
  #shop-ui .bv-shop-plate:not(.bv-shop-atelier) { max-height: calc(100dvh - 1.5rem); }
  .bv-shop-body .bv-shop-grid { min-height: 120px; }
}

/* ── The purchase prompt: above the stage, same worn plate ──────────────── */
.bv-shop-prompt { z-index: 10; background: rgba(6,4,2,0.72); }
.bv-shop-prompt-panel {
  padding: 16px 18px; max-height: 90dvh;
  background:
    linear-gradient(rgba(14,9,5,0.76), rgba(14,9,5,0.88)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 20% 30% / 420px 420px,
    url('/clutter/plate.webp') 0 0 / 420px 420px, #17110a;
  border-color: var(--brass);
}
.bv-shop-prompt-panel:focus { outline: none; }
.bv-shop-prompt-line { margin: 8px 0 0; color: var(--bone); font: 0.9rem/1.4 var(--font-body); }
.bv-shop-prompt-acts { justify-content: flex-end; flex-wrap: wrap; margin-top: 14px; gap: 8px; }
.bv-shop-prompt-acts .bv-act { min-height: 44px; min-width: 44px; touch-action: manipulation; }
`;
